import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Grid,
  Box,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  CircularProgress
} from '@mui/material';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';

// Lista de estados brasileiros
const estados = [
  'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS', 'MG',
  'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC', 'SP', 'SE', 'TO'
];

// Lista de profissões
const profissoes = [
  'Pedreiro',
  'Ajudante de Pedreiro',
  'Doméstica',
  'Cuidador de Idoso',
  'Babá',
  'Capinador',
  'Bombeiro',
  'Eletricista',
  'Carpinteiro',
  'Serralheiro',
  'Montador de Moveis',
  'Segurança',
  'Garçon',
  'Entregador',
  'Professo Particular',
  'Pintor',
  'Programador',
  'Fotógrafo',
  'Personal Trainner',
  'Contador',
  'Gesseiro'
];

const ProfessionalRegister = () => {
  const navigate = useNavigate();
  const { register } = useAuth();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [cities, setCities] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    phone: '',
    cpf: '',
    profession: '',
    description: '',
    address: {
      street: '',
      number: '',
      complement: '',
      neighborhood: '',
      city: '',
      state: '',
      zipCode: ''
    }
  });

  const [profileImage, setProfileImage] = useState(null);
  const [serviceImages, setServiceImages] = useState([]);
  const [profileImagePreview, setProfileImagePreview] = useState('');
  const [serviceImagesPreview, setServiceImagesPreview] = useState([]);
  const [success, setSuccess] = useState('');

  // Buscar cidades quando o estado for selecionado
  useEffect(() => {
    if (formData.address.state) {
      fetch(`https://servicodados.ibge.gov.br/api/v1/localidades/estados/${formData.address.state}/municipios`)
        .then(res => res.json())
        .then(data => setCities(data))
        .catch(error => console.error('Erro ao buscar cidades:', error));
    } else {
      setCities([]);
    }
  }, [formData.address.state]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    if (name.includes('.')) {
      const [parent, child] = name.split('.');
      setFormData(prev => ({
        ...prev,
        [parent]: {
          ...prev[parent],
          [child]: value
        }
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
  };

  const handleProfileImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.size > 30 * 1024 * 1024) {
        setError('A imagem de perfil deve ter no máximo 30MB');
        return;
      }
      setProfileImage(file);
      const reader = new FileReader();
      reader.onloadend = () => {
        setProfileImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const handleServiceImagesChange = (e) => {
    const files = Array.from(e.target.files);
    if (serviceImages.length + files.length > 10) {
      setError('Você pode adicionar no máximo 10 imagens de serviços');
      return;
    }

    const validFiles = files.filter(file => {
      if (file.size > 30 * 1024 * 1024) {
        setError('Cada imagem de serviço deve ter no máximo 30MB');
        return false;
      }
      return true;
    });

    setServiceImages(prev => [...prev, ...validFiles]);
    
    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        setServiceImagesPreview(prev => [...prev, reader.result]);
      };
      reader.readAsDataURL(file);
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      // Validar senhas
      if (formData.password !== formData.confirmPassword) {
        setError('As senhas não coincidem');
        setLoading(false);
        return;
      }

      // Validar campos obrigatórios
      const requiredFields = ['name', 'email', 'password', 'phone'];
      const missingFields = requiredFields.filter(field => !formData[field]);
      
      if (missingFields.length > 0) {
        setError(`Por favor, preencha todos os campos obrigatórios: ${missingFields.join(', ')}`);
        setLoading(false);
        return;
      }

      // Registrar usuário (dados completos)
      const registerResult = await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        phone: formData.phone,
        userType: 'provider',
        cpf: formData.cpf,
        profession: formData.profession,
        description: formData.description,
        address: {
          street: formData.address.street,
          number: formData.address.number,
          neighborhood: formData.address.neighborhood,
          city: formData.address.city,
          state: formData.address.state
        }
      });

      if (registerResult.success) {
        setSuccess('Cadastro realizado com sucesso! Agora escolha seu plano para ativar seu perfil.');
        setTimeout(() => navigate('/plans'), 2000);
      } else {
        setError(registerResult.message || 'Erro ao registrar profissional');
      }
    } catch (error) {
      console.error('Erro no registro:', error);
      setError(error.response?.data?.message || 'Erro ao registrar profissional');
    } finally {
      setLoading(false);
    }
  };

  const convertToBase64 = (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = () => resolve(reader.result);
      reader.onerror = error => reject(error);
    });
  };

  return (
    <Container maxWidth="md" sx={{ py: 4 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom align="center">
          Cadastro de Profissional
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}

        {success && (
          <Alert severity="success" sx={{ mb: 2 }}>
            {success}
          </Alert>
        )}

        <form onSubmit={handleSubmit}>
          <Grid container spacing={3}>
            {/* Campos básicos */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Nome completo"
                name="name"
                value={formData.name}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                type="email"
                value={formData.email}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Senha"
                name="password"
                type="password"
                value={formData.password}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Confirmar senha"
                name="confirmPassword"
                type="password"
                value={formData.confirmPassword}
                onChange={handleChange}
                required
              />
            </Grid>

            {/* Campos de contato */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Telefone"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                required
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="CPF"
                name="cpf"
                value={formData.cpf}
                onChange={handleChange}
                required
              />
            </Grid>

            {/* Campos profissionais */}
            <Grid item xs={12}>
              <FormControl fullWidth required>
                <InputLabel>Profissão</InputLabel>
                <Select
                  name="profession"
                  value={formData.profession}
                  onChange={handleChange}
                  label="Profissão"
                >
                  {profissoes.map((prof) => (
                    <MenuItem key={prof} value={prof}>
                      {prof}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descrição"
                name="description"
                multiline
                rows={4}
                value={formData.description}
                onChange={handleChange}
                required
              />
            </Grid>

            {/* Endereço */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Endereço
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Rua"
                name="address.street"
                value={formData.address.street}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Número"
                name="address.number"
                value={formData.address.number}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Complemento"
                name="address.complement"
                value={formData.address.complement}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Bairro"
                name="address.neighborhood"
                value={formData.address.neighborhood}
                onChange={handleChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Estado</InputLabel>
                <Select
                  name="address.state"
                  value={formData.address.state}
                  onChange={handleChange}
                  label="Estado"
                >
                  {estados.map((estado) => (
                    <MenuItem key={estado} value={estado}>
                      {estado}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <FormControl fullWidth>
                <InputLabel>Cidade</InputLabel>
                <Select
                  name="address.city"
                  value={formData.address.city}
                  onChange={handleChange}
                  label="Cidade"
                  disabled={!formData.address.state}
                >
                  {cities.map((city) => (
                    <MenuItem key={city.id} value={city.nome}>
                      {city.nome}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="CEP"
                name="address.zipCode"
                value={formData.address.zipCode}
                onChange={handleChange}
              />
            </Grid>

            {/* Upload de imagens */}
            <Grid item xs={12}>
              <Typography variant="h6" gutterBottom>
                Imagens
              </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
              <input
                accept="image/*"
                type="file"
                id="profile-image"
                onChange={handleProfileImageChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="profile-image">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                >
                  Foto de Perfil
                </Button>
              </label>
              {profileImagePreview && (
                <Box mt={2}>
                  <img
                    src={profileImagePreview}
                    alt="Preview"
                    style={{ maxWidth: '100%', maxHeight: '200px' }}
                  />
                </Box>
              )}
            </Grid>
            <Grid item xs={12} sm={6}>
              <input
                accept="image/*"
                type="file"
                id="service-images"
                multiple
                onChange={handleServiceImagesChange}
                style={{ display: 'none' }}
              />
              <label htmlFor="service-images">
                <Button
                  variant="outlined"
                  component="span"
                  fullWidth
                >
                  Fotos dos Serviços (máx. 10)
                </Button>
              </label>
              {serviceImagesPreview.length > 0 && (
                <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
                  {serviceImagesPreview.map((preview, index) => (
                    <img
                      key={index}
                      src={preview}
                      alt={`Preview ${index + 1}`}
                      style={{ maxWidth: '100px', maxHeight: '100px' }}
                    />
                  ))}
                </Box>
              )}
            </Grid>

            {/* Botão de submit */}
            <Grid item xs={12}>
              <Button
                type="submit"
                variant="contained"
                color="primary"
                fullWidth
                size="large"
                disabled={loading}
              >
                {loading ? <CircularProgress size={24} /> : 'Cadastrar'}
              </Button>
            </Grid>
          </Grid>
        </form>
      </Paper>
    </Container>
  );
};

export default ProfessionalRegister; 